// ============================================================
// CLUBHUB – Nachrichten: das kleine Markdown der Serienmail
// ============================================================
// Der Text einer Nachricht ist Markdown (`Mailing.body`). Die Masken
// brauchen davon genau das, was die Werkzeugleiste setzt: Absaetze,
// Zwischenueberschriften, **fett**, *kursiv*, [Verweise](https://…)
// und Listen. Mehr nicht — eine Vereinsmail ist kein Layoutprogramm.
//
// Ergebnis ist ein Baum, keine HTML-Zeichenkette: gezeichnet wird er
// von `components/nachrichten/markdown-text.tsx` mit React, das jeden
// Text maskiert. Fuer SMS gibt `alsText` den reinen Text.
// ============================================================

export type Inline =
  | { t: "text"; v: string }
  | { t: "b"; c: Inline[] }
  | { t: "i"; c: Inline[] }
  | { t: "a"; href: string; c: Inline[] }
  | { t: "br" };

export type Block =
  | { t: "p"; c: Inline[] }
  | { t: "h"; ebene: 2 | 3; c: Inline[] }
  | { t: "ul"; items: Inline[][] }
  | { t: "ol"; items: Inline[][] };

const INLINE = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|\*(?!\s)(.+?)\*|_(?!\s)(.+?)_/;

/** Nur Verweise, die eine Mail tragen darf. */
export function sichererVerweis(href: string): string | null {
  return /^(https?:\/\/|mailto:|tel:)/i.test(href) ? href : null;
}

export function inline(text: string): Inline[] {
  const teile: Inline[] = [];
  let rest = text;
  while (rest.length) {
    const m = INLINE.exec(rest);
    if (!m) {
      zeilen(rest, teile);
      break;
    }
    if (m.index > 0) zeilen(rest.slice(0, m.index), teile);
    if (m[1] !== undefined) teile.push({ t: "b", c: inline(m[1]) });
    else if (m[2] !== undefined) {
      const href = sichererVerweis(m[3]!);
      teile.push(href ? { t: "a", href, c: inline(m[2]) } : { t: "text", v: m[2] });
    } else if (m[4] !== undefined) teile.push({ t: "i", c: inline(m[4]) });
    else if (m[5] !== undefined) teile.push({ t: "i", c: inline(m[5]) });
    rest = rest.slice(m.index + m[0].length);
  }
  return teile;
}

/** Text mit Zeilenumbruechen innerhalb eines Absatzes. */
function zeilen(text: string, ziel: Inline[]) {
  text.split("\n").forEach((z, i) => {
    if (i > 0) ziel.push({ t: "br" });
    if (z) ziel.push({ t: "text", v: z });
  });
}

export function parse(quelle: string): Block[] {
  const bloecke: Block[] = [];
  const zeilenListe = quelle.replace(/\r\n?/g, "\n").split("\n");
  let absatz: string[] = [];
  let liste: { art: "ul" | "ol"; items: string[] } | null = null;

  const absatzZu = () => {
    if (absatz.length) bloecke.push({ t: "p", c: inline(absatz.join("\n")) });
    absatz = [];
  };
  const listeZu = () => {
    if (liste) bloecke.push({ t: liste.art, items: liste.items.map(inline) });
    liste = null;
  };

  for (const roh of zeilenListe) {
    const zeile = roh.trimEnd();
    const ul = /^\s*[-*•]\s+(.*)$/.exec(zeile);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(zeile);
    const h = /^(#{1,3})\s+(.*)$/.exec(zeile);
    if (!zeile.trim()) {
      absatzZu();
      listeZu();
    } else if (h) {
      absatzZu();
      listeZu();
      bloecke.push({ t: "h", ebene: h[1]!.length <= 2 ? 2 : 3, c: inline(h[2]!) });
    } else if (ul || ol) {
      absatzZu();
      const art = ul ? "ul" : "ol";
      if (liste && liste.art !== art) listeZu();
      if (!liste) liste = { art, items: [] };
      liste.items.push((ul ?? ol)![1]!);
    } else {
      listeZu();
      absatz.push(zeile);
    }
  }
  absatzZu();
  listeZu();
  return bloecke;
}

function inlineText(teile: Inline[]): string {
  return teile
    .map((x) =>
      x.t === "text" ? x.v : x.t === "br" ? "\n" : x.t === "a" ? `${inlineText(x.c)} (${x.href.replace(/^mailto:/, "")})` : inlineText(x.c),
    )
    .join("");
}

/** Der reine Text — fuer SMS, Vorschauzeilen und die Nur-Text-Fassung der Mail. */
export function alsText(quelle: string): string {
  return parse(quelle)
    .map((b) =>
      b.t === "p" || b.t === "h"
        ? inlineText(b.c)
        : b.items.map((it, i) => `${b.t === "ol" ? `${i + 1}.` : "-"} ${inlineText(it)}`).join("\n"),
    )
    .join("\n\n");
}

/** Die erste Zeile Text — fuer Listen („Liebe Lena, am 14. November …“). */
export function anriss(quelle: string, laenge = 120): string {
  const text = alsText(quelle).replace(/\s+/g, " ").trim();
  return text.length > laenge ? `${text.slice(0, laenge - 1).trimEnd()} …` : text;
}
